/** @jsxImportSource @emotion/react */
import type { FC, MouseEventHandler, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import type { ThemeColors } from '../../utils/color-utils';
import type { IconName } from '../Icon';
import ODSButton, { type ODSButtonProps } from '../ODSButton';
import ODSSpinner from '../ODSSpinner';
import { ODSModalBody, type ODSModalBodyProps } from './ODSModalBody';
import { ODSModalContainer, type ODSModalContainerProps } from './ODSModalContainer';
import { ODSModalFooter } from './ODSModalFooter';
import { ODSModalHeader, type ODSModalHeaderProps } from './ODSModalHeader';

export type ODSModalProps = Omit<ODSModalContainerProps, 'title'> & {
  /**
   * Pass in false so the modal doesn't close if the user clicks the backdrop, useful if they've
   * enter filled out a form and you don't want to lose it all if they accidentally click off
   * */
  canClickOffToDismiss?: boolean;
  /** Display a close btn at the top right */
  closeButton?: boolean;
  /** Does this modal perform a delete or permanent action */
  danger?: ODSButtonProps['danger'];
  /** Adds a red icon to the title  */
  error?: ODSModalHeaderProps['error'];
  /** Used to add something extra below the modal, like a coachmark.  */
  footer?: ReactNode;
  /** Footer content inserted at the left side of the footer */
  footerContent?: ReactNode;
  /** Hide the footer if you don't need it  */
  hideFooter?: boolean;
  /** Apply a light background for the heading */
  lightHeading?: ODSModalHeaderProps['light'];
  /** Message to display while loading */
  loadingMessage?: ReactNode;
  /** Don't perform primary action on enter press */
  noSubmitOnEnter?: boolean;
  /** What to do when they click anywhere in the modal */
  onClickModal?: ODSModalContainerProps['onClick'];
  /** Remove the default modal content padding */
  padding?: ODSModalBodyProps['padding'];
  /** Add a sub title below the title */
  subTitle?: ODSModalHeaderProps['subTitle'];
  /** Add a title to the modal */
  title?: ODSModalHeaderProps['title'];
  /** Add a yellow icon to the title */
  warning?: boolean;

  // Primary action props
  /** Display an icon in the primary button  */
  icon?: IconName;
  /** What to do when they click the primary button */
  onClick?: ODSButtonProps['onClick'];
  /** Pass in the button text for the cta, e.g. Done */
  primary?: ReactNode;
  /** Need a custom primary button color, pass it here */
  primaryColor?: ThemeColors | string;
  /** Need a custom primary cta, pass in your component here */
  customPrimary?: ReactNode;
  /** Disable the primary button */
  primaryDisabled?: boolean;

  // Secondary action props
  /** Need the secondary to do something other than close the modal? */
  onSecondaryClick?: ODSButtonProps['onClick'];
  /** Pass in the button text for the secondary button, e.g. Cancel */
  secondary?: ReactNode;

  // Tertiary action props
  /** What to do when they click the tertiary button, or button on the left side */
  onTertiaryClick?: ODSButtonProps['onClick'];
  /** Need a third action, pass in the button text */
  tertiary?: ReactNode;

  /**
   * Sim bucket for the submit (primary) button. Defaults to 'ignore' — most modal submit
   * actions require user-provided data the sim can't supply (file uploads, etc.). Pass
   * 'safe' or 'primary' for modals whose primary action is sim-safe.
   */
  submitSimBucket?: string;
};

/**
 * An opinionated modal component prewired for primary, secondary, and tertiary actions.
 */
export const ODSModal: FC<ODSModalProps> = ({
  canClickOffToDismiss = true,
  children,
  closeButton = true,
  danger,
  error,
  footer,
  footerContent,
  hideFooter,
  lightHeading = false,
  loadingMessage,
  noSubmitOnEnter,
  onClickModal,
  padding = true,
  subTitle,
  title,
  toggle,
  warning,
  // Primary action
  customPrimary,
  icon,
  onClick,
  primary,
  primaryColor,
  primaryDisabled,
  // Secondary action
  onSecondaryClick,
  secondary,
  // Tertiary action
  onTertiaryClick,
  tertiary,
  // Sim
  submitSimBucket = 'ignore',
  ...componentProps
}) => {
  const [loading, setLoading] = useState(false);

  const primaryCallback = useCallback(
    async (e) => {
      setLoading(true);
      await (onClick ?? toggle)?.(e);
      setLoading(false);
    },
    [onClick, toggle]
  );

  return (
    <ODSModalContainer
      {...componentProps}
      toggle={loading ? undefined : toggle}
      {...(!canClickOffToDismiss && { backdrop: 'static' })}
      onClick={onClickModal}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !noSubmitOnEnter) {
          void primaryCallback(e);
        }
      }}
    >
      {title && (
        <ODSModalHeader
          disabled={loading}
          error={error}
          hideClose={!closeButton}
          light={lightHeading}
          subTitle={subTitle}
          title={title}
          toggle={toggle}
          warning={warning}
        />
      )}
      <ODSModalBody
        padding={padding}
        css={{
          ...(!title && { borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }),
        }}
      >
        {children}
      </ODSModalBody>
      {!hideFooter && (
        <ODSModalFooter align={!!tertiary || !!footerContent || loading ? 'between' : 'end'}>
          {footerContent}
          {!!tertiary && (
            <ODSButton
              data-testid="modal-tertiary"
              outline
              onClick={onTertiaryClick as MouseEventHandler}
              disabled={loading}
            >
              {tertiary}
            </ODSButton>
          )}
          {loading && (
            <div className="d-flex align-items-center">
              <ODSSpinner />
              <span className="mx-2">{loadingMessage}</span>
            </div>
          )}
          <div className="d-flex gap-2">
            {!!secondary && (
              <ODSButton
                data-testid="modal-dismiss"
                quiet
                onClick={(onSecondaryClick ?? toggle) as MouseEventHandler}
                disabled={loading}
                data-sim="safe"
              >
                {secondary}
              </ODSButton>
            )}
            {!customPrimary ? (
              <ODSButton
                data-testid="modal-submit"
                cta={!primaryColor}
                data-sim={submitSimBucket}
                danger={danger}
                warning={warning}
                onClick={primaryCallback}
                disabled={Boolean(primaryDisabled) || loading}
                icon={icon}
                color={primaryColor}
              >
                {primary}
              </ODSButton>
            ) : (
              customPrimary
            )}
          </div>
        </ODSModalFooter>
      )}
      {!!footer && footer}
    </ODSModalContainer>
  );
};

export default ODSModal;
